import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Chip from "@material-ui/core/Chip";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import EditIcon from "@material-ui/icons/EditOutlined";
import FileCopyIcon from "@material-ui/icons/FileCopyOutlined";
import SendIcon from "@material-ui/icons/SendOutlined";
import SyncAltIcon from "@material-ui/icons/SyncAltOutlined";
import HistoryIcon from "@material-ui/icons/HistoryOutlined";
import ReplayIcon from "@material-ui/icons/Replay";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { useConfirm } from "../../components/ConfirmDialog";
import { useMenuStyles } from "../../components/menuStyles";

const useStyles = makeStyles(theme => ({
  page: { padding: theme.spacing(3) },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, marginBottom: theme.spacing(0.5) },
  subtitle: { color: theme.app.textTertiary, marginBottom: theme.spacing(2) },
  head: { display: "flex", alignItems: "center", marginBottom: theme.spacing(2) },
  paper: { boxShadow: "none", border: `1px solid ${theme.app.border}`, borderRadius: 12 },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.app.border}`,
    "&:last-child": { borderBottom: "none" },
  },
  url: { fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  secret: { fontFamily: "monospace", fontSize: 12, color: theme.app.textTertiary },
  chips: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 },
  meta: { fontSize: 12, color: theme.app.textTertiary, width: 130, textAlign: "right" },
  empty: { padding: theme.spacing(5, 2), textAlign: "center", color: theme.app.textTertiary },
  off: { opacity: 0.5 },
  groupLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.app.textTertiary, margin: theme.spacing(1.5, 0, 0.5) },
  secretBox: { fontFamily: "monospace", fontSize: 13, background: "#1e1e1e", color: "#d4f7c5", padding: theme.spacing(1.5), borderRadius: 8, wordBreak: "break-all" },
  dot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block", marginRight: 6 },
  dRow: { borderBottom: `1px solid ${theme.app.lineSoft}`, padding: "8px 0", "&:last-child": { borderBottom: "none" } },
  dHead: { display: "flex", alignItems: "center", gap: 10, cursor: "pointer" },
  dStatus: { fontFamily: theme.app.mono, fontSize: 11.5, fontWeight: 600, padding: "1px 7px", borderRadius: 4 },
  dOk: { background: "#E6F4EC", color: theme.app.ok },
  dFail: { background: "#F7E3E1", color: theme.app.danger },
  dType: { fontFamily: theme.app.mono, fontSize: 12.5, color: theme.app.ink, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  dMeta: { fontSize: 11.5, color: theme.app.textTertiary, fontFamily: theme.app.mono },
  dBody: { marginTop: 8, background: "#161B21", color: "#E6E9ED", borderRadius: 8, padding: "10px 12px", fontFamily: theme.app.mono, fontSize: 11.5, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 180, overflowY: "auto" },
  dBodyLbl: { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.app.textTertiary, fontWeight: 700, margin: "8px 0 4px" },
}));

const fmtDate = d => {
  if (!d) return "nunca";
  try {
    return new Date(d).toLocaleString("pt-BR");
  } catch (e) {
    return "—";
  }
};

const healthColor = status => {
  if (status == null) return "#c9c7bd"; // sem entrega ainda
  if (status >= 200 && status < 300) return "#2B8A3E";
  return "#b00020";
};

const Webhooks = () => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const { confirm } = useConfirm();
  const [endpoints, setEndpoints] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editor, setEditor] = useState(null); // { id?, url, events:[] }
  const [reveal, setReveal] = useState(null); // { secret }
  const [deliveries, setDeliveries] = useState(null); // { endpoint, list, loading }
  const [openDelivery, setOpenDelivery] = useState(null); // id da entrega expandida

  const load = useCallback(async () => {
    try {
      const [e, c] = await Promise.all([api.get("/webhooks"), api.get("/webhooks/events")]);
      setEndpoints(e.data.endpoints || []);
      setCatalog(c.data.events || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = catalog.reduce((acc, s) => {
    (acc[s.group] = acc[s.group] || []).push(s);
    return acc;
  }, {});

  const labelOf = key => {
    if (key === "*") return "Todos os eventos";
    const f = catalog.find(s => s.key === key);
    return f ? f.label : key;
  };

  const openNew = () => setEditor({ url: "", events: [] });
  const openEdit = e => setEditor({ id: e.id, url: e.url, events: e.events || [] });

  const toggleEvent = key =>
    setEditor(e => ({
      ...e,
      events: e.events.includes(key) ? e.events.filter(s => s !== key) : [...e.events, key],
    }));

  const save = async () => {
    if (!editor.url.trim()) return;
    try {
      if (editor.id) {
        await api.put(`/webhooks/${editor.id}`, { url: editor.url.trim(), events: editor.events });
        setEditor(null);
        load();
      } else {
        const { data } = await api.post("/webhooks", { url: editor.url.trim(), events: editor.events });
        setEditor(null);
        setReveal({ secret: data.secret });
        load();
      }
    } catch (err) {
      toastError(err);
    }
  };

  const toggleActive = async e => {
    try {
      await api.put(`/webhooks/${e.id}`, { active: !e.active });
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const regenerate = async e => {
    if (!(await confirm({ title: "Regenerar secret", message: "Gerar um novo secret de assinatura? Atualize a verificação no seu servidor.", confirmText: "Regenerar", danger: true }))) return;
    try {
      const { data } = await api.post(`/webhooks/${e.id}/regenerate`);
      setReveal({ secret: data.secret });
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const showDeliveries = async e => {
    setDeliveries({ endpoint: e, list: [], loading: true });
    setOpenDelivery(null);
    try {
      const { data } = await api.get(`/webhooks/${e.id}/deliveries`);
      setDeliveries({ endpoint: e, list: data.deliveries || [], loading: false });
    } catch (err) {
      toastError(err);
      setDeliveries(null);
    }
  };

  const reloadDeliveries = async () => {
    if (!deliveries) return;
    try {
      const { data } = await api.get(`/webhooks/${deliveries.endpoint.id}/deliveries`);
      setDeliveries(d => (d ? { ...d, list: data.deliveries || [] } : d));
    } catch (err) {
      /* silencioso */
    }
  };

  const replay = async d => {
    try {
      await api.post(`/webhooks/deliveries/${d.id}/replay`);
      toast.success("Reenviado.");
      setTimeout(reloadDeliveries, 1200);
    } catch (err) {
      toastError(err);
    }
  };

  const sendTest = async e => {
    try {
      await api.post(`/webhooks/${e.id}/test`);
      toast.success("Evento de teste enviado.");
      setTimeout(load, 1500);
    } catch (err) {
      toastError(err);
    }
  };

  const remove = async e => {
    if (!(await confirm({ title: "Excluir webhook", message: `Excluir o webhook para ${e.url}?`, confirmText: "Excluir", danger: true }))) return;
    try {
      await api.delete(`/webhooks/${e.id}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const copy = txt => {
    try {
      navigator.clipboard.writeText(txt);
      toast.success("Copiado!");
    } catch (e) {
      /* clipboard blocked */
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.head}>
        <div style={{ flex: 1 }}>
          <Typography variant="h5" className={classes.title}>Webhooks</Typography>
          <Typography className={classes.subtitle}>
            Receba eventos (chamada atendida/encerrada, mensagem recebida…) no seu servidor. Cada entrega é
            assinada com HMAC-SHA256 no header <code>X-Numera-Signature</code>.
          </Typography>
        </div>
        <Button variant="contained" color="primary" startIcon={<SyncAltIcon />} onClick={openNew}>
          Novo webhook
        </Button>
      </div>

      <Paper className={classes.paper}>
        {endpoints.length === 0 ? (
          <div className={classes.empty}>Nenhum webhook ainda. Crie um para receber eventos.</div>
        ) : (
          endpoints.map(e => (
            <div key={e.id} className={`${classes.row} ${e.active ? "" : classes.off}`}>
              <span className={classes.dot} style={{ background: healthColor(e.lastStatus) }} title={`Última entrega: ${e.lastStatus == null ? "nenhuma" : e.lastStatus}`} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={classes.url}>{e.url}</div>
                <div className={classes.secret}>{e.secretMasked}</div>
                <div className={classes.chips}>
                  {(e.events || []).length === 0 ? (
                    <span style={{ fontSize: 12, color: "#b00" }}>sem eventos</span>
                  ) : (
                    e.events.map(s => <Chip key={s} size="small" label={labelOf(s)} style={{ height: 20 }} />)
                  )}
                </div>
              </div>
              <span className={classes.meta}>última entrega<br />{fmtDate(e.lastDeliveryAt)}</span>
              <Switch size="small" color="primary" checked={!!e.active} onChange={() => toggleActive(e)} title={e.active ? "Ativo" : "Inativo"} />
              <IconButton size="small" title="Enviar teste" onClick={() => sendTest(e)}>
                <SendIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" title="Histórico de entregas" onClick={() => showDeliveries(e)}>
                <HistoryIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" title="Editar" onClick={() => openEdit(e)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" title="Regenerar secret" onClick={() => regenerate(e)}>
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" title="Excluir" onClick={() => remove(e)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </div>
          ))
        )}
      </Paper>

      {/* criar / editar */}
      <Dialog open={!!editor} onClose={() => setEditor(null)} maxWidth="sm" fullWidth PaperProps={{ className: menu.dialogPaper }}>
        <DialogTitle>{editor && editor.id ? "Editar webhook" : "Novo webhook"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            size="small"
            label="URL do endpoint (https://…)"
            placeholder="https://seu-servidor.com/numera/webhook"
            value={editor ? editor.url : ""}
            onChange={e => {
              const v = e.target.value;
              setEditor(s => ({ ...s, url: v }));
            }}
          />
          <div className={classes.groupLabel}>Eventos</div>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={!!editor && editor.events.includes("*")}
                onChange={() => toggleEvent("*")}
              />
            }
            label="Todos os eventos (inclui futuros)"
            style={{ display: "block", marginLeft: 0 }}
          />
          {Object.keys(groups).map(g => (
            <div key={g}>
              <div className={classes.groupLabel}>{g}</div>
              {groups[g].map(s => (
                <FormControlLabel
                  key={s.key}
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      disabled={!!editor && editor.events.includes("*")}
                      checked={!!editor && (editor.events.includes(s.key) || editor.events.includes("*"))}
                      onChange={() => toggleEvent(s.key)}
                    />
                  }
                  label={s.label}
                  style={{ display: "block", marginLeft: 0 }}
                />
              ))}
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(null)}>Cancelar</Button>
          <Button onClick={save} variant="contained" color="primary" disabled={!editor || !editor.url.trim()}>
            {editor && editor.id ? "Salvar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* signing secret mostrado uma única vez */}
      <Dialog open={!!reveal} onClose={() => setReveal(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Secret de assinatura</DialogTitle>
        <DialogContent>
          <Typography style={{ marginBottom: 12, fontSize: 14 }}>
            Use este secret para verificar o header <code>X-Numera-Signature</code> (HMAC-SHA256 do corpo).
            Mostramos <b>uma única vez</b>.
          </Typography>
          <div className={classes.secretBox}>{reveal && reveal.secret}</div>
          <Button startIcon={<FileCopyIcon />} onClick={() => copy(reveal.secret)} style={{ marginTop: 12 }}>
            Copiar
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReveal(null)} variant="contained" color="secondary">
            Guardei, fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* histórico de entregas + replay */}
      <Dialog open={!!deliveries} onClose={() => setDeliveries(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Entregas
          {deliveries && <div style={{ fontSize: 12, fontWeight: 400, color: "#888", fontFamily: "monospace", marginTop: 2 }}>{deliveries.endpoint.url}</div>}
        </DialogTitle>
        <DialogContent>
          {deliveries && deliveries.loading ? (
            <div className={classes.empty}>Carregando…</div>
          ) : deliveries && deliveries.list.length === 0 ? (
            <div className={classes.empty}>Nenhuma entrega ainda. Use "Enviar teste" ou dispare um evento.</div>
          ) : (
            deliveries &&
            deliveries.list.map(d => (
              <div key={d.id} className={classes.dRow}>
                <div className={classes.dHead} onClick={() => setOpenDelivery(openDelivery === d.id ? null : d.id)}>
                  <span className={`${classes.dStatus} ${d.success ? classes.dOk : classes.dFail}`}>
                    {d.statusCode || "erro"}
                  </span>
                  <span className={classes.dType}>{d.eventType === "*" ? "teste" : d.eventType}</span>
                  {d.attempt > 1 && <span className={classes.dMeta}>tentativa {d.attempt}</span>}
                  <span className={classes.dMeta}>{fmtDate(d.createdAt)}</span>
                  <IconButton size="small" title="Reenviar" onClick={ev => { ev.stopPropagation(); replay(d); }}>
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </div>
                {openDelivery === d.id && (
                  <div>
                    {d.errorMessage && (
                      <>
                        <div className={classes.dBodyLbl}>Erro</div>
                        <div className={classes.dBody} style={{ color: "#ffb4ad" }}>{d.errorMessage}</div>
                      </>
                    )}
                    <div className={classes.dBodyLbl}>Payload enviado</div>
                    <div className={classes.dBody}>{d.requestBody || "—"}</div>
                    <div className={classes.dBodyLbl}>Resposta</div>
                    <div className={classes.dBody}>{d.responseBody || "(vazia)"}</div>
                  </div>
                )}
              </div>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveries(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Webhooks;
